import { useCallback, useId, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import {
  createDomainRequest,
  domainErrorMessage,
  domainFormFrom,
  updateDomainRequest,
  validateDomainForm,
  EMPTY_DOMAIN_FORM,
  type DomainFormErrors,
  type DomainFormField,
  type DomainFormValues,
} from '@/lib/domainForm';
import type { DomainDetail } from '@/types/domain';
import type { TLSMode } from '@/types/tlsSettings';

/** null keeps the dialog closed. */
export type DomainDialogTarget = { mode: 'create' } | { mode: 'edit'; domain: DomainDetail } | null;

interface DomainDialogProps {
  target: DomainDialogTarget;
  /** No domain exists yet: the new one becomes the default whatever is chosen. */
  firstDomain: boolean;
  onClose: () => void;
  onSaved: (domain: DomainDetail) => void;
}

/** Shell always mounted; the form mounts only while a target is set, so state resets on every open. */
export function DomainDialog({ target, firstDomain, onClose, onSaved }: DomainDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {target ? (
        <FormBody
          key={target.mode === 'edit' ? `edit:${target.domain.id}` : 'create'}
          target={target}
          firstDomain={firstDomain}
          onClose={onClose}
          onSaved={onSaved}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormBodyProps extends Omit<DomainDialogProps, 'target'> {
  target: NonNullable<DomainDialogTarget>;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

const checkboxClass = 'size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring';

const MODES: { value: TLSMode; title: string; body: string }[] = [
  {
    value: 'letsencrypt',
    title: "Let's Encrypt",
    body: 'DNS-only record; Traefik obtains a certificate per app through the Cloudflare DNS challenge.',
  },
  {
    value: 'origin',
    title: 'Cloudflare origin certificate',
    body: 'Proxied record; Traefik serves the uploaded origin certificate. Needs SSL mode Full (strict).',
  },
];

function FormBody({ target, firstDomain, onClose, onSaved, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const editing = target.mode === 'edit' ? target.domain : null;
  const ids = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLInputElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<DomainFormValues>(() => (editing ? domainFormFrom(editing) : EMPTY_DOMAIN_FORM));
  const [errors, setErrors] = useState<DomainFormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  // LE is unavailable when Traefik's token cannot read the zone (known only once the domain exists).
  const leBlocked = editing !== null && !editing.letsencrypt_capable && editing.new_app_tls_mode !== 'letsencrypt';
  const defaultLocked = firstDomain || (editing?.is_default ?? false);

  const change = (patch: Partial<DomainFormValues>, field?: DomainFormField) => {
    setForm((f) => ({ ...f, ...patch }));
    if (field) setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  const invalidProps = (field: DomainFormField) =>
    errors[field] ? { 'aria-invalid': true, 'aria-describedby': `${ids}-${field}-error` } : {};

  const fieldMessage = (field: DomainFormField) =>
    errors[field] ? (
      <p id={`${ids}-${field}-error`} className="text-xs text-destructive">
        {errors[field]}
      </p>
    ) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const invalid = validateDomainForm(form, editing ? 'edit' : 'create', editing?.zone_id);
    const first = (['name', 'zone_id', 'api_token'] as const).find((f) => invalid[f]);
    if (first) {
      setError(null);
      setErrors(invalid);
      (first === 'name' ? nameRef : first === 'zone_id' ? zoneRef : tokenRef).current?.focus();
      return;
    }
    setErrors({});
    let send: () => Promise<DomainDetail>;
    if (editing) {
      const changes = updateDomainRequest(editing, form);
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      send = () => api.domains.update(editing.id, changes);
    } else {
      send = () => api.domains.create(createDomainRequest(form));
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await send();
      toast.success(editing ? 'Domain saved' : `Domain ${saved.name} added`);
      onSaved(saved);
    } catch (err) {
      setError(domainErrorMessage(err, editing ? 'Failed to save domain' : 'Failed to add domain'));
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        (editing ? zoneRef : nameRef).current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add domain'}</DialogTitle>
        <DialogDescription>
          {editing
            ? 'The name is fixed: app hostnames are built from it. Zone and token changes are checked with Cloudflare.'
            : 'A Cloudflare zone apps get hostnames under. The bridge checks the token can read the zone before saving.'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        {editing ? null : (
          <div className="space-y-2">
            <label htmlFor={`${ids}-name`} className="text-sm font-medium">
              Domain
            </label>
            <Input
              ref={nameRef}
              id={`${ids}-name`}
              value={form.name}
              onChange={(e) => change({ name: e.target.value }, 'name')}
              {...invalidProps('name')}
              required
              maxLength={253}
              spellCheck={false}
              autoCapitalize="none"
              placeholder="apps.example.com"
              className="font-mono text-sm"
            />
            {fieldMessage('name')}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor={`${ids}-zone_id`} className="text-sm font-medium">
            Cloudflare zone ID
          </label>
          <Input
            ref={zoneRef}
            id={`${ids}-zone_id`}
            value={form.zone_id}
            onChange={(e) => change({ zone_id: e.target.value }, 'zone_id')}
            {...invalidProps('zone_id')}
            aria-describedby={errors.zone_id ? `${ids}-zone_id-error` : `${ids}-zone-hint`}
            required
            maxLength={64}
            spellCheck={false}
            autoCapitalize="none"
            placeholder="023e105f4ecef8ad9ca31a8372d0c353"
            className="font-mono text-sm"
          />
          {fieldMessage('zone_id')}
          <p id={`${ids}-zone-hint`} className="text-xs text-muted-foreground">
            Cloudflare → the domain’s overview page → API → Zone ID.
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor={`${ids}-api_token`} className="text-sm font-medium">
            Cloudflare API token
          </label>
          <Input
            ref={tokenRef}
            id={`${ids}-api_token`}
            type="password"
            autoComplete="off"
            value={form.api_token}
            onChange={(e) => change({ api_token: e.target.value }, 'api_token')}
            {...invalidProps('api_token')}
            aria-describedby={errors.api_token ? `${ids}-api_token-error` : `${ids}-token-hint`}
            required={!editing}
            spellCheck={false}
            placeholder={editing ? 'Leave blank to keep the current token' : undefined}
          />
          {fieldMessage('api_token')}
          <p id={`${ids}-token-hint`} className="text-xs text-muted-foreground">
            Needs Zone:Read and DNS:Edit on this zone. Stored encrypted and never shown again.
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">TLS for new apps</legend>
          <p className="text-xs text-muted-foreground">Existing apps keep the mode they were created with.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {MODES.map((m) => {
              const disabled = m.value === 'letsencrypt' && leBlocked;
              return (
                <label
                  key={m.value}
                  className="flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm has-[:checked]:border-primary has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
                >
                  <input
                    type="radio"
                    name={`${ids}-mode`}
                    value={m.value}
                    checked={form.new_app_tls_mode === m.value}
                    disabled={disabled}
                    onChange={() => change({ new_app_tls_mode: m.value })}
                    aria-describedby={`${ids}-mode-${m.value}`}
                    className={`mt-0.5 ${checkboxClass}`}
                  />
                  <span>
                    <span className="font-medium">{m.title}</span>
                    <span id={`${ids}-mode-${m.value}`} className="mt-0.5 block text-xs text-muted-foreground">
                      {m.body}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {leBlocked ? (
            <p className="text-xs text-muted-foreground">
              Let's Encrypt is unavailable: Traefik's Cloudflare token (CLOUDFLARE_API_TOKEN) cannot manage this zone.
            </p>
          ) : null}
        </fieldset>

        <div className="space-y-1">
          <label className="flex cursor-pointer items-center gap-2 select-none has-[:disabled]:cursor-not-allowed">
            <input
              type="checkbox"
              checked={defaultLocked || form.is_default}
              disabled={defaultLocked}
              onChange={(e) => change({ is_default: e.target.checked })}
              aria-describedby={`${ids}-default-hint`}
              className={checkboxClass}
            />
            <span className="text-sm font-medium">Default domain</span>
          </label>
          <p id={`${ids}-default-hint`} className="pl-6 text-xs text-muted-foreground">
            {firstDomain
              ? 'The first domain is always the default.'
              : editing?.is_default
                ? 'To change the default, make another domain the default.'
                : 'Apps deployed without a domain get a hostname here.'}
          </p>
        </div>

        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none [overflow-wrap:anywhere]">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? (editing ? 'Saving…' : 'Checking…') : editing ? 'Save' : 'Add domain'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
